import { Button, Select, TextInput, Box, Group } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import LoadingData from '@components/graphql-loading-data';
import GraphqlError from '@components/graphql-error';
import { ClientStatus } from '@generated/graphql';
import { UPSERT_CLIENT, GET_CLIENTS } from '@queries/clients';

function ClientNew() {
  const { clientId } = useParams();
  const navigate = useNavigate();

  const form = useForm({
    validateInputOnChange: true,
    initialValues: {
      firstName: '',
      lastName: '',
      status: '',
      email: '',
      phone: '',
      avatar: '',
    },
    validate: {
      firstName: (value) => (value.trim().length < 1 ? 'Please enter a first name' : null),
      lastName: (value) => (value.trim().length < 1 ? 'Please enter a last name' : null),
      status: (value) => (value.trim().length < 1 ? 'Please select a status' : null),
      email: (value) => (value === null || value === '' || /^\S+@\S+$/.test(value) ? null : 'Invalid email address'),
      phone: (value) =>
        value === null || value === '' || /^\d{3}-\d{3}-\d{4}$/.test(value)
          ? null
          : 'Invalid phone number format (e.g., 321-555-1234)',
    },
  });

  const [upsertClient, { loading, error }] = useMutation(UPSERT_CLIENT, {
    refetchQueries: [{ query: GET_CLIENTS, variables: { clientId } }],
    awaitRefetchQueries: true,
    errorPolicy: 'all',
  });
  if (loading) return <LoadingData />;
  if (error) return <GraphqlError error={error} />;

  type FormValues = {
    firstName: string;
    lastName: string;
    status: string;
    email: string;
    phone: string;
    avatar: string;
  };

  const saveClient = async (values: FormValues) => {
    const result = await upsertClient({
      variables: {
        firstName: values.firstName,
        lastName: values.lastName,
        status: ClientStatus[values.status as keyof typeof ClientStatus],
        email: values.email,
        phone: values.phone,
        avatar: values.avatar,
      },
    });
    const clientId = result.data?.upsertClient.id;
    if (clientId) navigate(`/clients/${clientId}`);
    else navigate(-1);
  };

  const cancel = () => {
    navigate(-1);
  };

  return (
    <Box maw={500} mx="auto">
      <form onSubmit={form.onSubmit(saveClient)}>
        <TextInput label="First Name" placeholder="first name" {...form.getInputProps('firstName')} />
        <TextInput label="Last Name" placeholder="last name" {...form.getInputProps('lastName')} />
        <TextInput label="Phone Number" placeholder="321-555-1234" {...form.getInputProps('phone')} />
        <TextInput label="Email" placeholder="email@example.com" {...form.getInputProps('email')} />
        <TextInput label="Avatar" placeholder="<url>" {...form.getInputProps('avatar')} />
        <Select
          mt="md"
          data={Object.keys(ClientStatus)}
          placeholder="Pick one"
          label="Client Status"
          {...form.getInputProps('status')}
        />
        <Group mt="md">
          <Button type="submit">Submit</Button>
          <Button onClick={cancel}>Cancel</Button>
        </Group>
      </form>
    </Box>
  );
}

export default ClientNew;
